import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import LeopartLoader from "../../../shared/LeopartLoader/LeopartLoader";
import { Search, Play, Dumbbell, LayoutGrid, Video, ChevronLeft, ChevronRight } from 'lucide-react';
import { useEndpoints } from '../../../hooks/useEndpoints';
import { IProgrammingExercise, IProgrammingGroupExercise } from '../../../models/programming';
import { useDebouncedValue } from '../../../hooks/useDebouncedValue';
import GenericModalV2 from '../../../shared/GenericModal/GenericModal';
import styles from './ExercisesTab.module.scss';

function getYoutubeThumbnail(url: string): string | null {
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
    /youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/,
  ];
  for (const regex of patterns) {
    const match = url.match(regex);
    if (match?.[1]) return `https://img.youtube.com/vi/${match[1]}/mqdefault.jpg`;
  }
  return null;
}

const COLOR_PALETTE = [
  { bg: '#effafe', text: '#1a8fb5', border: '#d4eef7' },
  { bg: '#e6f5f4', text: '#1a8f6e', border: '#c8e8e5' },
  { bg: '#fff3e0', text: '#d4860b', border: '#ffe0b2' },
  { bg: '#fce4ec', text: '#c62828', border: '#f8bbd0' },
  { bg: '#f3e5f5', text: '#7b1fa2', border: '#e1bee7' },
  { bg: '#e8eaf6', text: '#3949ab', border: '#c5cae9' },
  { bg: '#e0f2f1', text: '#00796b', border: '#b2dfdb' },
  { bg: '#fff8e1', text: '#f9a825', border: '#ffecb3' },
];

let groupColorMap: Record<string, { bg: string; text: string; border: string }> = {};
let colorIndex = 0;

function getGroupColor(groupId: string) {
  if (!groupColorMap[groupId]) {
    groupColorMap[groupId] = COLOR_PALETTE[colorIndex % COLOR_PALETTE.length];
    colorIndex++;
  }
  return groupColorMap[groupId];
}

const ExercisesTab = () => {
  const { getProgrammingExercises, getProgrammingExerciseGroups } = useEndpoints();
  const [search, setSearch] = useState('');
  const [selectedGroup, setSelectedGroup] = useState('');
  const [page, setPage] = useState(1);
  const [selectedExercise, setSelectedExercise] = useState<IProgrammingExercise | null>(null);
  const debouncedSearch = useDebouncedValue(search, 300);

  const groupsQuery = useQuery({
    queryKey: ['programming-exercise-groups'],
    queryFn: async () => {
      const { data } = await getProgrammingExerciseGroups();
      return data;
    },
    staleTime: 10 * 60 * 1000,
  });

  const exercisesQuery = useQuery({
    queryKey: ['programming-exercises', debouncedSearch, selectedGroup, page],
    queryFn: async () => {
      const { data } = await getProgrammingExercises({
        search: debouncedSearch || undefined,
        group: selectedGroup || undefined,
        lang: 'it',
        page,
        limit: 24,
      });
      return data;
    },
    staleTime: 5 * 60 * 1000,
  });

  const groups = useMemo(() => groupsQuery.data ?? [], [groupsQuery.data]);
  const exercises = useMemo(() => exercisesQuery.data?.docs ?? [], [exercisesQuery.data]);
  const totalPages = exercisesQuery.data?.totalPages ?? 1;
  const totalDocs = exercisesQuery.data?.totalDocs ?? 0;

  const getExerciseName = (ex: IProgrammingExercise) =>
    ex.name.it || ex.name.en || '—';

  const getGroupName = (g: IProgrammingGroupExercise) =>
    g.name.it || g.name.en || '—';

  const startItem = (page - 1) * 24 + 1;
  const endItem = Math.min(page * 24, totalDocs);

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <h2 className={styles.headerTitle}>Libreria esercizi</h2>
          {!exercisesQuery.isLoading && totalDocs > 0 && (
            <span className={styles.headerCount}>{totalDocs}</span>
          )}
        </div>
      </div>

      {/* Search + filters */}
      <div className={styles.filtersCard}>
        <div className={styles.searchWrapper}>
          <Search className={styles.searchIcon} size={16} />
          <input
            type="text"
            placeholder="Cerca esercizio per nome..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className={styles.searchInput}
          />
        </div>

        {groups.length > 0 && (
          <div className={styles.groupPillsRow}>
            <button
              className={`${styles.groupPill} ${selectedGroup === '' ? styles.groupPillActive : ''}`}
              onClick={() => { setSelectedGroup(''); setPage(1); }}
            >
              <LayoutGrid size={13} />
              Tutti
            </button>
            {groups.map((g) => {
              const color = getGroupColor(g._id);
              const isActive = selectedGroup === g._id;
              return (
                <button
                  key={g._id}
                  className={`${styles.groupPill} ${isActive ? styles.groupPillActive : ''}`}
                  style={isActive ? undefined : { background: color.bg, borderColor: color.border, color: color.text }}
                  onClick={() => { setSelectedGroup(g._id); setPage(1); }}
                >
                  {getGroupName(g)}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Content */}
      {exercisesQuery.isLoading ? (
        <div className={styles.loadingState}>
          <LeopartLoader />
        </div>
      ) : exercises.length === 0 ? (
        <div className={styles.emptyState}>
          <div className={styles.emptyIconWrap}>
            <Search size={24} />
          </div>
          <p className={styles.emptyTitle}>Nessun esercizio trovato</p>
          <p className={styles.emptySubtitle}>Prova a cambiare i filtri di ricerca</p>
        </div>
      ) : (
        <>
          <div className={styles.grid}>
            {exercises.map((ex) => {
              const hasVideo = !!ex.url;
              const thumbnail = ex.url ? getYoutubeThumbnail(ex.url) : null;
              return (
                <div
                  key={ex._id}
                  className={styles.card}
                  onClick={() => setSelectedExercise(ex)}
                >
                  {/* Thumbnail */}
                  {thumbnail ? (
                    <div className={styles.cardThumbImg}>
                      <img src={thumbnail} alt="" className={styles.thumbImage} />
                      <div className={styles.playOverlay}>
                        <Play size={24} fill="#fff" className={styles.playIcon} />
                      </div>
                    </div>
                  ) : (
                    <div className={styles.cardThumb}>
                      {hasVideo ? (
                        <Video size={18} className={styles.thumbIconVideo} />
                      ) : (
                        <Dumbbell size={18} className={styles.thumbIcon} />
                      )}
                    </div>
                  )}

                  <div className={styles.cardBody}>
                    <p className={styles.exerciseName}>{getExerciseName(ex)}</p>

                    {ex.groups.length > 0 && (
                      <div className={styles.groupBadges}>
                        {ex.groups.map((g) => {
                          const color = getGroupColor(g._id);
                          return (
                            <span
                              key={g._id}
                              className={styles.groupBadge}
                              style={{ background: color.bg, color: color.text, borderColor: color.border }}
                            >
                              {getGroupName(g)}
                            </span>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {totalPages > 1 && (
            <div className={styles.paginationRow}>
              <span className={styles.paginationInfo}>
                {startItem}–{endItem} di {totalDocs}
              </span>
              <div className={styles.paginationButtons}>
                <button
                  className={styles.pageBtn}
                  disabled={page <= 1}
                  onClick={() => setPage((p) => p - 1)}
                >
                  <ChevronLeft size={16} />
                </button>
                <span className={styles.pageCounter}>
                  {page} / {totalPages}
                </span>
                <button
                  className={styles.pageBtn}
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Detail modal */}
      <GenericModalV2
        isOpen={!!selectedExercise}
        onClose={() => setSelectedExercise(null)}
        title={selectedExercise ? getExerciseName(selectedExercise) : ''}
        size="lg"
      >
        {selectedExercise && (
          <div className={styles.detailContent}>
            {selectedExercise.url && (
              <div className={styles.videoWrapper}>
                <iframe
                  src={selectedExercise.url}
                  title={getExerciseName(selectedExercise)}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            )}
            {selectedExercise.groups.length > 0 && (
              <div className={styles.detailSection}>
                <p className={styles.detailLabel}>Gruppi muscolari</p>
                <div className={styles.groupBadges}>
                  {selectedExercise.groups.map((g) => {
                    const color = getGroupColor(g._id);
                    return (
                      <span
                        key={g._id}
                        className={styles.groupBadge}
                        style={{ background: color.bg, color: color.text, borderColor: color.border }}
                      >
                        {getGroupName(g)}
                      </span>
                    );
                  })}
                </div>
              </div>
            )}
            {(selectedExercise.description.it || selectedExercise.description.en) && (
              <div className={styles.detailSection}>
                <p className={styles.detailLabel}>Descrizione</p>
                <p className={styles.detailDescription}>
                  {selectedExercise.description.it || selectedExercise.description.en}
                </p>
              </div>
            )}
          </div>
        )}
      </GenericModalV2>
    </div>
  );
};

export default ExercisesTab;
